import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, Globe } from "lucide-react";
import { COMPANY } from "../data/site";

const FOOTER_INDUSTRIES = [
  "Oil & Gas",
  "Petrochemical",
  "Waste to Energy",
  "Valve Manufacturing",
  "CRA Pipe Manufacturing",
  "Power Generation",
  "Heavy Engineering"
];

const QUICK_LINKS = [
  { to: "/products", label: "Products" },
  { to: "/industries", label: "Industries" },
  { to: "/#projects", label: "Projects" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" }
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_weld-overlay-tech/artifacts/y6ehzf11_Weldmatix%20Fusion%20Logo.png";

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-[#F8FAFC] border-t border-[#E2E8F0] text-[#334155]">
      {/* CTA strip — dark on top */}
      <div className="bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="font-mono-wm text-[11px] tracking-[0.3em] uppercase text-[#0EA5E9] mb-3">Engineering Partnership</div>
            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-white leading-[1.05]">
              Request Technical Consultation
            </h3>
            <p className="text-white/70 mt-4 max-w-lg">
              Talk to our engineering team about your weld overlay, pipe cladding or
              welding automation requirements.
            </p>
          </div>
          <div className="flex flex-col md:flex-row md:justify-end gap-3">
            <Link
              to="/contact"
              data-testid="footer-cta-consult"
              className="group inline-flex items-center justify-between gap-4 bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-6 py-4 text-sm uppercase tracking-[0.2em] font-medium transition-colors min-w-[260px]"
            >
              Request Consultation
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
            <a
              href={COMPANY.whatsapp_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cta-whatsapp"
              className="inline-flex items-center justify-between gap-4 border border-white/25 hover:border-white text-white px-6 py-4 text-sm uppercase tracking-[0.2em] transition-colors min-w-[260px]"
            >
              WhatsApp Sales
              <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Main footer — light */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-12 gap-10">
        {/* Brand block */}
        <div className="col-span-2 md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO_URL} alt="Weldmatix Fusion" className="logo-img h-14 w-auto object-contain" />
            <div className="leading-tight border-l border-[#E2E8F0] pl-3 ml-1">
              <div className="font-display font-bold text-[#0F172A] text-base tracking-tight">WELDMATIX FUSION</div>
              <div className="font-mono-wm text-[10px] tracking-[0.3em] text-[#64748B] uppercase">Welding Automation OEM</div>
            </div>
          </div>
          <div className="font-display text-[#0F172A] text-lg leading-tight max-w-sm">
            Advanced Welding Automation & Weld Overlay Solutions
          </div>
          <p className="mt-3 text-sm text-[#475569] leading-relaxed max-w-sm">
            Engineering precision cladding systems, welding automation equipment, and
            industrial solutions for critical industries worldwide.
          </p>
          <div className="mt-6 flex items-start gap-2 text-sm text-[#0F172A]">
            <MapPin className="w-4 h-4 text-[#0EA5E9] mt-0.5 flex-shrink-0" />
            <span>Navi Mumbai, Maharashtra, India</span>
          </div>
        </div>

        {/* Industries Served */}
        <div className="md:col-span-3">
          <div className="font-mono-wm text-[11px] tracking-[0.3em] uppercase text-[#0EA5E9] mb-5">Industries Served</div>
          <ul className="space-y-2.5">
            {FOOTER_INDUSTRIES.map((i) => (
              <li key={i} className="text-sm text-[#334155] flex items-start gap-2">
                <span className="text-[#0EA5E9] mt-0.5">•</span>
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Links */}
        <div className="md:col-span-2">
          <div className="font-mono-wm text-[11px] tracking-[0.3em] uppercase text-[#0EA5E9] mb-5">Quick Links</div>
          <ul className="space-y-2.5">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`} className="text-sm text-[#334155] hover:text-[#0EA5E9]">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="col-span-2 md:col-span-3">
          <div className="font-mono-wm text-[11px] tracking-[0.3em] uppercase text-[#0EA5E9] mb-5">Contact</div>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={`mailto:${COMPANY.email_info}`} className="flex items-center gap-2.5 text-[#334155] hover:text-[#0EA5E9]" data-testid="footer-email-info">
                <Mail className="w-4 h-4 text-[#0EA5E9]" /> {COMPANY.email_info}
              </a>
            </li>
            <li>
              <a href={`mailto:${COMPANY.email_sales}`} className="flex items-center gap-2.5 text-[#334155] hover:text-[#0EA5E9]" data-testid="footer-email-sales">
                <Mail className="w-4 h-4 text-[#0EA5E9]" /> {COMPANY.email_sales}
              </a>
            </li>
            <li>
              <a href="https://www.weldmatixco.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-[#334155] hover:text-[#0EA5E9]">
                <Globe className="w-4 h-4 text-[#0EA5E9]" /> www.weldmatixco.com
              </a>
            </li>
            <li>
              <a href={COMPANY.whatsapp_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-[#334155] hover:text-[#0EA5E9]">
                <Phone className="w-4 h-4 text-[#0EA5E9]" /> {COMPANY.whatsapp}
              </a>
            </li>
          </ul>

          <Link
            to="/contact"
            data-testid="footer-inline-cta"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] uppercase tracking-[0.15em]"
          >
            Request Technical Consultation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Corporate Affiliation — subtle */}
      <div className="border-t border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <a
            href={COMPANY.clan_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="clan-group-affiliation"
            className="group flex items-center gap-3 text-[12px] text-[#64748B] hover:text-[#0F172A] transition-colors"
          >
            <img
              src={COMPANY.clan_logo}
              alt="Clan Group"
              className="logo-img h-8 w-8 object-contain"
              data-testid="clan-group-logo"
            />
            <span className="font-mono-wm tracking-[0.18em] uppercase">A member of the Clan Group ecosystem</span>
          </a>
          <div className="font-mono-wm text-[11px] tracking-[0.2em] uppercase text-[#64748B] text-center md:text-right">
            © {new Date().getFullYear()} Weldmatix Fusion. All Rights Reserved. · Designed for global industrial markets.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
